const LOCATION_CACHE_KEY = 'lastKnownLocation';

export const parseAddress = addressData => {
  if (!addressData) return 'Location not available';

  if (typeof addressData === 'string') {
    const parts = addressData.split(',').map(p => p.trim());
    if (parts.length > 3) {
      const state = parts.find(p => p.toLowerCase().includes('pradesh') || p.toLowerCase().includes('delhi'));
      const city = parts[parts.length - 4];
      if (city && state) return `${city}, ${state}`;
      return `${parts[1]}, ${parts[2]}`;
    }
    return addressData;
  }

  const { city, town, village, county, state_district, state } = addressData || {};
  const primaryLocation = city || town || village || county || state_district;
  const secondaryLocation = state || '';

  if (primaryLocation && secondaryLocation && primaryLocation !== secondaryLocation) {
    return `${primaryLocation}, ${secondaryLocation}`;
  }
  return primaryLocation || 'Unknown Location';
};

const fetchAddressFromCoords = (lat, lon) => {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`;
  return fetch(url)
    .then(response => response.json())
    .then(data => {
      if (data && data.address) {
        return {
          displayName: parseAddress(data.address),
          raw: data.address
        };
      }
      return { displayName: null, raw: null };
    });
};

const saveLocationToCache = locationData => {
  const dataWithTimestamp = {
    ...locationData,
    timestamp: Date.now()
  };
  localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(dataWithTimestamp));
};

const getLocationFromCache = () => {
  const cached = localStorage.getItem(LOCATION_CACHE_KEY);
  if (!cached) return null;

  const data = JSON.parse(cached);
  if (data.timestamp && Date.now() - data.timestamp > 300000) {
    localStorage.removeItem(LOCATION_CACHE_KEY);
    return null;
  }
  return data;
};

export const getCurrentPosition = (retryCount = 0) => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    const cachedLocation = getLocationFromCache();

    const handleSuccess = position => {
      const { latitude, longitude, altitude, accuracy } = position.coords;
      if (navigator.onLine) {
        fetchAddressFromCoords(latitude, longitude)
          .then(locationDetails => {
            const fullLocation = {
              latitude,
              longitude,
              altitude: altitude || 0,
              accuracy: accuracy || 0,
              address: locationDetails.displayName,
              city: locationDetails.raw?.city || null,
              rawAddress: locationDetails.raw
            };
            saveLocationToCache(fullLocation);
            resolve(fullLocation);
          })
          .catch(() => {
            const fullLocation = { latitude, longitude, address: null, city: null };
            saveLocationToCache(fullLocation);
            resolve(fullLocation);
          });
      } else {
        const fullLocation = {
          latitude,
          longitude,
          altitude: altitude || 0,
          accuracy: accuracy || 0,
          address: null,
          city: null
        };
        saveLocationToCache(fullLocation);
        resolve(fullLocation);
      }
    };

    const handleError = error => {
      // Permission denied must always reject immediately — never fall back to cache
      if (error.code === 1) {
        reject({ code: 'PERMISSION_DENIED', message: 'Location permission denied by user.' });
        return;
      }

      if (cachedLocation && cachedLocation.latitude) {
        resolve(cachedLocation);
        return;
      }

      if (retryCount < 2) {
        setTimeout(() => {
          getCurrentPosition(retryCount + 1)
            .then(resolve)
            .catch(reject);
        }, 1000);
      } else {
        if (error.code === 2 || error.code === 3) {
          reject({ code: 'POSITION_UNAVAILABLE', message: 'Location information is unavailable.' });
        } else {
          resolve(null);
        }
      }
    };

    const options = retryCount === 0 ? { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 } : { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 };

    navigator.geolocation.getCurrentPosition(handleSuccess, handleError, options);
  });
};
